<script setup>
import { computed, getCurrentInstance, toRef } from 'vue';
import { useDialogA11y } from 'dashboard/composables/useDialogA11y';

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: '' },
  closeLabel: { type: String, default: 'Fechar' },
  snackbarOffset: { type: [Number, String], default: 0 },
});

const emit = defineEmits(['close']);
const { uid } = getCurrentInstance();
const overlayStyle = { backgroundColor: 'rgba(15, 23, 42, 0.42)' };
const titleId = `lais-bottom-sheet-${uid}`;
const labelledBy = computed(() => (props.title ? titleId : undefined));
const sheetStyle = computed(() => {
  const offset = Number(props.snackbarOffset) || 0;
  return offset > 0 ? { marginBottom: `${offset}px` } : {};
});
const { dialogRef } = useDialogA11y({
  isOpen: toRef(props, 'show'),
  onClose: () => emit('close'),
});
</script>

<template>
  <div>
    <div
      v-if="show"
      class="fixed inset-0 z-lais-overlay"
      :style="overlayStyle"
      @click.self="emit('close')"
    >
      <Transition name="lais-bottom-sheet" appear>
        <section
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="labelledBy"
          tabindex="-1"
          :style="sheetStyle"
          class="absolute inset-x-0 bottom-0 flex max-h-[88vh] min-h-0 flex-col rounded-t-lais-sheet border border-b-0 border-lais-border bg-white shadow-lais-pop outline-none sm:inset-y-0 sm:left-auto sm:right-0 sm:bottom-auto sm:h-full sm:w-full sm:max-w-lg sm:rounded-none sm:border-y-0 sm:border-l"
        >
          <div
            class="mx-auto mt-3 h-1.5 w-14 shrink-0 rounded-full bg-lais-border sm:hidden"
          />
          <header
            class="flex shrink-0 items-center justify-between gap-3 border-b border-lais-border-soft px-5 py-4"
          >
            <div class="min-w-0 flex-1">
              <slot name="header">
                <h2
                  v-if="title"
                  :id="titleId"
                  class="font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
                >
                  {{ title }}
                </h2>
              </slot>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-items-center rounded-full text-lais-text-muted transition hover:bg-lais-primary-50 hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              :aria-label="closeLabel"
              @click="emit('close')"
            >
              <span class="i-lucide-x size-4" aria-hidden="true" />
            </button>
          </header>
          <div class="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <slot />
          </div>
          <footer
            v-if="$slots.footer"
            class="shrink-0 border-t border-lais-border-soft px-5 py-4"
          >
            <slot name="footer" />
          </footer>
        </section>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.lais-bottom-sheet-enter-active,
.lais-bottom-sheet-leave-active {
  transition:
    opacity 160ms ease,
    transform 180ms ease;
}

.lais-bottom-sheet-enter-from,
.lais-bottom-sheet-leave-to {
  opacity: 0;
  transform: translateY(24px);
}

@media (min-width: 640px) {
  .lais-bottom-sheet-enter-from,
  .lais-bottom-sheet-leave-to {
    transform: translateX(24px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .lais-bottom-sheet-enter-active,
  .lais-bottom-sheet-leave-active {
    transition: opacity 0.001ms;
    transform: none;
  }
}
</style>
